import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/Logo";

export const Navbar = () => {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const items = (settings?.menu || []).filter((m) => m && m.enabled !== false);

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-cyan-400" : "text-slate-300 hover:text-white"}`;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0B0F17]/95" data-testid="site-navbar">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-12">
        <Link to="/" data-testid="header-logo-link" className="shrink-0" aria-label="Cyber Planet home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {items.map((m) => (
            <NavLink key={m.path + m.label} to={m.path} className={linkClass} data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g, "-")}-link`}>
              {m.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-ink transition hover:bg-cyan-300"
          >
            Start a project
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          className="rounded-lg p-2 text-slate-200 hover:bg-white/5 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-white/5 bg-[#0B0F17]/95 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {items.map((m) => (
                <NavLink
                  key={m.path + m.label}
                  to={m.path}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 text-base ${isActive ? "bg-white/5 text-cyan-400" : "text-slate-200 hover:bg-white/5"}`
                  }
                  data-testid={`nav-mobile-${m.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                >
                  {m.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-2 block rounded-full bg-cyan-400 px-5 py-3 text-center text-sm font-semibold text-ink"
                data-testid="nav-mobile-cta-button"
              >
                Start a project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
